import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ColumnsType } from "antd/es/table";
import {Space, Table, Button, Input, message, Select, TimePicker} from "antd";
import {
    addActivityRow,
    deleteActivityByActid,
    fetchTotalActivityHours,
    fetchTotalDrillingHours,
    getAllActivitiesAndBreakdownsByPid,
    sortActivitiesByShift,
    updateActivityRow
} from "../../../../backendInteraction/ActivitiesApiFetch";
import { fetchSelectOptionsByContractNO } from "../../../../backendInteraction/ApiFetch";
import {
    calculateDurationHours,
    formatDateToClockTime,
    numberValidator,
    parseClockTimeToDate, WorkingTimeValueValidator
} from "../../../../utils/Validator";
import {
    addBreakdownRow, deleteBreakdownByBid,
    updateBreakdownRow
} from "../../../../backendInteraction/BreakdownsApiFetch";
import {ActivityAndBreakdownData} from "../../../../types/ActivityAndBreakdownData";
import dayjs from "dayjs";

interface ActivitiesTableProps {
    data: ActivityAndBreakdownData[];
    plodId: number;
    contractNo: string;
    isDayShift: boolean;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
    holeId: string;
    setHoleId: (value: string) => void;
    onTotalHoursUpdate?: (total: number) => void;
    onTotalDrillingHoursUpdate?: (total: number) => void;
}

const ActivitiesTable: React.FC<ActivitiesTableProps> =
    ({ data, plodId, contractNo, isDayShift , isView, isAddNewRow,
         setIsAddNewRow, holeId, setHoleId, onTotalHoursUpdate, onTotalDrillingHoursUpdate
    }) => {

    const [activitiesData, setActivitiesData] = useState<ActivityAndBreakdownData[]>([]);
    const [activityOptions, setActivityOptions] = useState<{ value: string; label: string }[]>([]);
    const [editingActid, setEditingActid] = useState<number | null>(null);
    const [editData, setEditData] = useState<ActivityAndBreakdownData | null>(null);

    const loadActivities = useCallback(async () => {
        if (contractNo) {
            const activities = await fetchSelectOptionsByContractNO(contractNo, "Activity");
            const breakdowns = await fetchSelectOptionsByContractNO(contractNo,"Breakdowns");

            setActivityOptions([
                ...activities.map((activity) => ({
                    value: `ACTIVITY:${activity}`,
                    label: activity,
                })),
                ...breakdowns.map((breakdown) => ({
                    value: `BREAKDOWN:${breakdown}`,
                    label: `Breakdown : ${breakdown}`,
                })),
            ]);

        } else {
            setActivityOptions([]);
        }
    }, [contractNo]);

    useEffect(() => {
        loadActivities();
    }, [loadActivities]);

    useEffect(() => {
        setActivitiesData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingActid !== -1) {
            const currentDate = new Date();
            const defaultMeridiem = currentDate.getHours() >= 6 && currentDate.getHours() < 18 ? 'Day' : 'Night';

            const len = activitiesData.length;
            const newRow: ActivityAndBreakdownData = {
                key: -1,
                aob_id: -1,
                hole_id: len > 0 ? activitiesData[len - 1].hole_id || holeId : holeId,
                aob_type: "",
                time_start: len > 0 ? activitiesData[len - 1].time_finish : '',
                time_finish: "",
                total_hours: "",
                pid: plodId ?? -1,
            };
            setActivitiesData(prevData => {
                const hasNewRow = prevData.some(item => item.aob_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            });

            setEditingActid(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingActid]);

    useEffect(() => {
        if (isView) {
            /*console.log("Exiting edit mode due to external update.");*/
            if (editingActid === -1) {
                setActivitiesData(prevData => prevData.filter(item => item.aob_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingActid(null);
            setEditData(null);
        }
    }, [isView]);

    useEffect(() => {
        const fetchData = async () => {
            const storedUser = localStorage.getItem("DORS_USER");
            const aid = storedUser && JSON.parse(storedUser).Aid;

            try {
                const activitiesAndBreakdowns =
                    await getAllActivitiesAndBreakdownsByPid(plodId ?? -1, aid);
                setActivitiesData(sortActivitiesByShift(activitiesAndBreakdowns, isDayShift));
            } catch (error) {
                console.error("Error fetching activities when switching shift value:", error);
            }
        };
        fetchData();
    }, [isDayShift, plodId]);

    const saveChanges = useCallback(async () => {
        console.log("Saving changes:", editData);

        if (!editData) return;

        const { aob_type, time_start, time_finish, total_hours, hole_id } = editData;

        if (!aob_type) {
            message.error('Must select an activity!');
            return
        }

        if (!WorkingTimeValueValidator(time_start, time_finish)) return;

        const [typePrefix, realOption] = aob_type.split(':');
        const start = parseClockTimeToDate(time_start);
        const finish = parseClockTimeToDate(time_finish);
        const duration = time_start && time_finish ? calculateDurationHours(start, finish) : null;
        const formattedStart = formatDateToClockTime(start);
        const formattedFinish = formatDateToClockTime(finish);


        /*console.log('editData', editData, 'typePrefix', typePrefix, 'realOption', realOption);*/

        if (!!total_hours) {
            const isNumber = numberValidator(total_hours, 'Hours');
            if (!isNumber) return;
            const hoursVal = parseFloat(total_hours);
            if (hoursVal < 0 || hoursVal > 24) {
                message.error('The "Hours" value is invalid. Please enter a number between 0 and 24.');
                return;
            }
            if (hoursVal > 12) {
                message.info('The duration of this activity exceeds 12 hours!');
            }
        }

        if (!!duration && parseFloat(duration) > 12)  message.info('The duration of this activity exceeds 12 hours!');

        const updatedEditData = { ...editData, aob_type: realOption, time_start: formattedStart || '',
            time_finish: formattedFinish || '', total_hours: duration || `${parseFloat(total_hours)}` || '' };
        setEditData(updatedEditData);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (typePrefix === 'ACTIVITY') {
            if (editingActid === -1) {
                const success = await addActivityRow(updatedEditData, aid);
                if (success) {
                    const activitiesAndBreakdowns = await getAllActivitiesAndBreakdownsByPid(plodId ?? -1, aid);
                    setActivitiesData(sortActivitiesByShift(activitiesAndBreakdowns, isDayShift));
                    message.success("New activity added successfully!");

                    if (onTotalHoursUpdate) {
                        const newTotal = await fetchTotalActivityHours(plodId, true, aid);
                        onTotalHoursUpdate(newTotal);
                    }

                    if (onTotalDrillingHoursUpdate) {
                        const newTotal = await fetchTotalDrillingHours(plodId, aid);
                        onTotalDrillingHoursUpdate(newTotal);
                    }

                } else {
                    message.error("Failed to add new activity.");
                }
            } else {
                const success = await updateActivityRow(editingActid ?? -1, updatedEditData);
                if (success) {
                    const activitiesAndBreakdowns = await getAllActivitiesAndBreakdownsByPid(plodId ?? -1, aid);
                    setActivitiesData(sortActivitiesByShift(activitiesAndBreakdowns, isDayShift));
                    message.success("Activity updated successfully!");

                    if (onTotalHoursUpdate) {
                        const newTotal = await fetchTotalActivityHours(plodId, true, aid);
                        onTotalHoursUpdate(newTotal);
                    }

                    if (onTotalDrillingHoursUpdate) {
                        const newTotal = await fetchTotalDrillingHours(plodId, aid);
                        onTotalDrillingHoursUpdate(newTotal);
                    }

                } else {
                    message.error("Failed to update activity.");
                }
            }
        } else {
            if (editingActid === -1) {
                const success = await addBreakdownRow(updatedEditData, aid);
                if (success) {
                    const activitiesAndBreakdowns = await getAllActivitiesAndBreakdownsByPid(plodId ?? -1, aid);
                    setActivitiesData(sortActivitiesByShift(activitiesAndBreakdowns, isDayShift));
                    message.success("New breakdown added successfully!");

                    if (onTotalHoursUpdate) {
                        const newTotal = await fetchTotalActivityHours(plodId, true, aid);
                        onTotalHoursUpdate(newTotal);
                    }

                    if (onTotalDrillingHoursUpdate) {
                        const newTotal = await fetchTotalDrillingHours(plodId, aid);
                        onTotalDrillingHoursUpdate(newTotal);
                    }

                } else {
                    message.error("Failed to add new breakdown.");
                }
            } else {
                const success = await updateBreakdownRow(editingActid ?? -1, updatedEditData);
                if (success) {
                    const activitiesAndBreakdowns = await getAllActivitiesAndBreakdownsByPid(plodId ?? -1, aid);
                    setActivitiesData(sortActivitiesByShift(activitiesAndBreakdowns, isDayShift));
                    message.success("Breakdown updated successfully!");

                    if (onTotalHoursUpdate) {
                        const newTotal = await fetchTotalActivityHours(plodId, true, aid);
                        onTotalHoursUpdate(newTotal);
                    }

                    if (onTotalDrillingHoursUpdate) {
                        const newTotal = await fetchTotalDrillingHours(plodId, aid);
                        onTotalDrillingHoursUpdate(newTotal);
                    }

                } else {
                    message.error("Failed to update breakdown.");
                }
            }
        }

        !(hole_id === holeId) && setHoleId(hole_id);

        if (editingActid === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingActid(null);
        setEditData(null);
    }, [editData, editingActid, setIsAddNewRow]);

    const cancelEditing = useCallback((record: ActivityAndBreakdownData) => {
        console.log("Cancel editing");
        setEditingActid(null);
        setEditData(null);

        if (record.aob_id === -1) {
            setActivitiesData(prevData => prevData.filter(item => item.aob_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: ActivityAndBreakdownData) => {
        console.log("Editing row:", record);

        if (editingActid === -1) {
            setActivitiesData(prevData => prevData.filter(item => item.aob_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingActid(prevId => {
            if (prevId !== null) cancelEditing({ aob_id: prevId } as ActivityAndBreakdownData);
            return record.aob_id;
        });

        setEditData({ ...record });
    }, [editingActid, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: ActivityAndBreakdownData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const { aob_type } = record;

        const [typePrefix, realOption] = aob_type.split(':');

        if (typePrefix === 'ACTIVITY') {
            const success = await deleteActivityByActid(record.aob_id);
            if (success) {
                const activitiesAndBreakdowns = await getAllActivitiesAndBreakdownsByPid(plodId ?? -1, aid);
                setActivitiesData(sortActivitiesByShift(activitiesAndBreakdowns, isDayShift));
                message.success("Activity deleted successfully!");

                if (onTotalHoursUpdate) {
                    const newTotal = await fetchTotalActivityHours(plodId, true, aid);
                    onTotalHoursUpdate(newTotal);
                }

                if (onTotalDrillingHoursUpdate) {
                    const newTotal = await fetchTotalDrillingHours(plodId, aid);
                    onTotalDrillingHoursUpdate(newTotal);
                }

            } else {
                message.error("Failed to delete activity.");
            }
        } else {
            const success = await deleteBreakdownByBid(record.aob_id);
            if (success) {
                const activitiesAndBreakdowns = await getAllActivitiesAndBreakdownsByPid(plodId ?? -1, aid);
                setActivitiesData(sortActivitiesByShift(activitiesAndBreakdowns, isDayShift));
                message.success("Breakdown deleted successfully!");

                if (onTotalHoursUpdate) {
                    const newTotal = await fetchTotalActivityHours(plodId, true, aid);
                    onTotalHoursUpdate(newTotal);
                }

                if (onTotalDrillingHoursUpdate) {
                    const newTotal = await fetchTotalDrillingHours(plodId, aid);
                    onTotalDrillingHoursUpdate(newTotal);
                }

            } else {
                message.error("Failed to delete breakdown.");
            }
        }
    }, []);

    const handleInputChange = useCallback((key: keyof ActivityAndBreakdownData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);

    const formatAobType = (aobType?: string | null) => {
        if (!aobType || !aobType.includes(":")) return aobType;
        const [type, label] = aobType.split(":");
        return type?.toUpperCase() === "ACTIVITY" ? label : `Breakdown: ${label}`;
    };

    const columns: ColumnsType<ActivityAndBreakdownData> = useMemo(() => [
        {
            title: "Hole ID",
            dataIndex: "hole_id",
            key: "hole_id",
            width: "12%",
            render: (_, record) =>
                editingActid === record.aob_id ? (
                    <Input
                        value={editData?.hole_id}
                        onChange={(e) => handleInputChange("hole_id", e.target.value)}
                    />
                ) : (
                    record.hole_id
                ),
        },
        {
            title: "Activity",
            dataIndex: "aob_type",
            key: "aob_type",
            width: "25%",
            minWidth: 280,
            render: (_, record) =>
                editingActid === record.aob_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={activityOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.aob_type || ""}
                        onChange={(value) => handleInputChange("aob_type", value || "")}
                    />
                ) : (
                    formatAobType(record.aob_type)
                ),
        },
        {
            title: "Time Start",
            dataIndex: "time_start",
            key: "time_start",
            width: "18%",
            render: (_, record) =>
                editingActid === record.aob_id ? (
                    <TimePicker
                        use12Hours
                        format="h:mm a"
                        minuteStep={15}
                        value={editData?.time_start ? dayjs(editData.time_start, "h:mm a") : null}
                        onChange={(_, timeString) =>
                            handleInputChange("time_start", typeof timeString === "string" ? timeString : "")
                        }
                    />
                ) : (
                    record.time_start
                ),
        },
        {
            title: "Time Finish",
            dataIndex: "time_finish",
            key: "hours",
            width: "18%",
            render: (_, record) =>
                editingActid === record.aob_id ? (
                    <TimePicker
                        use12Hours
                        format="h:mm a"
                        minuteStep={15}
                        value={editData?.time_finish ? dayjs(editData.time_finish, "h:mm a") : null}
                        onChange={(_, timeString) =>
                            handleInputChange("time_finish", typeof timeString === "string" ? timeString : "")
                        }
                    />
                ) : (
                    record.time_finish
                ),
        },
        {
            title: "Hours",
            dataIndex: "total_hours",
            key: "total_hours",
            width: "12%",
            render: (_, record) =>
                editingActid === record.aob_id ? (
                    <Input
                        value={editData?.total_hours}
                        onChange={(e) => handleInputChange("total_hours", e.target.value)}
                    />
                ) : (
                    record.total_hours
                ),
        },
        ...(!isView
            ? [
                {
                    title: "Action",
                    key: "action",
                    width: "15%",
                    render: (_, record) => (
                        <Space size="small">
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingActid === record.aob_id ? saveChanges() : startEditing(record))}>
                                {editingActid === record.aob_id ? "Save" : "Edit"}
                            </Button>
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingActid === record.aob_id ? cancelEditing(record) : deleteRow(record))}>
                                {editingActid === record.aob_id ? "Cancel" : "Delete"}
                            </Button>
                        </Space>
                    ),
                },
            ] as ColumnsType<ActivityAndBreakdownData>
            : []),
    ], [isView, editingActid, activityOptions, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<ActivityAndBreakdownData>
            columns={columns}
            pagination={false}
            dataSource={activitiesData}
        />
    );
};

export default ActivitiesTable;
